// Englische Fassung von ../de/calendar.ts.
//
// Absichtlich NICHT uebersetzt (und deshalb hier woertlich deutsch):
//   * `TT.MM.JJJJ` — die Eingabemaske selbst. Das Datumsformat bleibt deutsch
//     (docs/i18n.md §2), also bleibt auch seine Schreibweise die deutsche; eine
//     zweite Notation („DD.MM.YYYY") waere eine zweite Form derselben Sache.
//   * „Uhr" hinter einer Uhrzeit — dieselbe Klasse wie die Wochentags- und
//     Monatsnamen, die aus der `de-DE`-Formatierung fallen und deutsch bleiben.
//   * Produkt- und Eigennamen: Outlook, Teams, Salesforce, Atlas, bitop.
import type { CalendarKatalog } from "../de/calendar";

export const calendar: CalendarKatalog = {
  seitenTitel: "Calendar",
  heute: "Today",
  keineTermine: "No events",

  seite: {
    ansichtAria: "View",
    tag: "Day",
    woche: "Week",
    monat: "Month",
    agenda: "Agenda",
    vor: "Forward",
    neuerTermin: "New event",
    terminFinden: "Find a time",
    abstimmungen: "Polls",
    weitereBefehle: "More commands",
    nichtErreichbarTitel: "Calendar unavailable",
    nichtErreichbarHinweis:
      "Your events cannot be loaded right now. Please try again in a moment.",
    nichtFreigeschaltetTitel: "Calendar not enabled yet",
    nichtFreigeschaltetHinweis:
      "As soon as your mailbox is activated for Atlas, your events will appear here.",
  },

  termin: {
    ganztaegig: "All day",
    keinBetreff: "(no subject)",
    abgesagt: "Cancelled",
    tagOeffnen: "Open day",
    mehr: (n: number) => `+${n} more`,
    keineTermineNaechste14: "No events in the next 14 days",

    zusagen: "Accept",
    mitVorbehalt: "Tentative",
    absagen: "Decline",

    erinnerung: "Reminder",
    erinnerungTitel: "Reminder before the event starts",
    erinnerungKeine: "None",
    erinnerungMinuten: (n: number) => `${n} min`,
    erinnerungEinTag: "1 day",

    keineWeiterenTeilnehmer: "No other attendees",
    teilnehmerAnzahl: (n: number) => `Attendees (${n})`,
    wenigerAnzeigen: "show fewer",
    weitereAnzeigen: (n: number) => `+${n} more`,
    teamsBeitreten: "Join Teams meeting",
    antwortAnOrganisator: "Reply to the organiser",
    keineTerminId:
      "This event carries no event ID — replying, editing and deleting are not possible for it.",

    antwortZugesagt: "accepted",
    antwortAbgesagt: "declined",
    antwortMitVorbehalt: "tentative",
    antwortOrganisator: "Organiser",
    antwortOffen: "open",

    respondZugesagt: "Accepted",
    respondAbgesagt: "Declined",
    respondMitVorbehalt: "Accepted tentatively",
    antwortFehlgeschlagen: "Reply failed",
    dienstNichtErreichbarKurz: "Calendar service unavailable.",

    loeschenTitel: "Delete event?",
    loeschenFrage: (betreff: string) =>
      `"${betreff}" will be deleted. This cannot be undone.`,
    geloescht: "Event deleted",
  },

  bearbeiten: {
    titel: "Edit event",
    betreff: "Subject",
    beginnDatum: "Start (date)",
    beginnUhrzeit: "Start (time)",
    ariaBeginnDatum: "Start date (TT.MM.JJJJ)",
    ariaBeginnUhrzeit: "Start time",
    endeDatum: "End (date)",
    endeUhrzeit: "End (time)",
    ariaEndeDatum: "End date (TT.MM.JJJJ)",
    ariaEndeUhrzeit: "End time",
    bitteTtMmJjjj: "Please enter as TT.MM.JJJJ",
    platzhalterDatum: "TT.MM.JJJJ",
    endeNachBeginn: "The end must be after the start.",
    aktualisiert: "Event updated",
    aenderungGespeichert: "The change has been saved.",
    keinZugriff: "No access to this calendar.",
    dienstNichtErreichbar: "Calendar service unavailable. Please try again.",
    speichernFehlgeschlagen: "Saving failed.",
  },

  neu: {
    titel: "New event",
    datumUnvollstaendig: "Date incomplete",
    zeitspanneUngueltig: "Time span invalid",
    einladungSenden: "Send invitation",
    terminAnlegen: "Create event",
    betreff: "Subject",
    betreffPlatzhalter: "e.g. registration alignment",
    datum: "Date",
    ariaDatum: "Date (TT.MM.JJJJ)",
    beginn: "Start",
    ariaStartzeit: "Start time",
    dauer: "Duration",
    ariaDauer: "Duration",
    enddatum: "End date",
    enddatumHinweis: "Only for multi-day events — otherwise leave it empty.",
    ariaEnddatum: "End date (TT.MM.JJJJ, only for multi-day events)",
    mitternachtBeginn: "Start",
    mitternachtRest1:
      "goes past midnight. For a multi-day event that is the",
    mitternachtTaeglich: "daily",
    mitternachtRest2:
      "time span — shorten the duration or leave the end date empty.",
    teilnehmerEinladen: "Invite attendees (just me for now)",
    teilnehmerEingeladen: (n: number) => `${n} attendees invited`,
    extern: (n: number) => `${n} external`,
    zuklappen: "Collapse",
    auswaehlen: "Select",
    onlineMeeting: "Online meeting (Teams)",
    beschreibung: "Description",
    beschreibungPlatzhalter: "Short context …",
    endeNachBeginn: "The end must be after the start.",
    fehlerNichtEinladbar:
      "At least one attendee address cannot be invited (neither an active bitop colleague nor a Salesforce contact).",
    fehlerDienst: "Calendar service unavailable. Please try again.",
    fehlerAnlegen: "The event could not be created.",
    einladungGesendet: "Invitation sent",
    angelegt: "Event created",
    teamsLinkImTermin: "The Teams link is in the event.",
  },

  picker: {
    suchePlatzhalter: "Type a name or e-mail …",
    sucheAria: "Search attendees (colleagues and external contacts)",
    badgeExtern: "External",
    badgeFunktionspostfach: "Shared mailbox",
    weitererAccount: (n: number) => `+${n} more account`,
    kollegenlisteAusblenden: "Hide colleague list",
    alleKollegenAnzeigen: "Show all colleagues",
    selbstNichtInListe:
      "You are not in the list yourself — as the organiser you are included automatically.",
    selbstNichtEinladbar:
      "You cannot invite yourself — as the organiser you are included automatically.",
    keineWeiterenKollegen: "No other colleagues.",
    mindestensZeichen: (n: number) =>
      `At least ${n} characters — colleagues and external Salesforce contacts.`,
    wirdGesucht: "Searching …",
    nichtInSalesforce:
      "is not stored as a contact in Salesforce. Colleagues and Salesforce contacts can be invited — without a contact record the server rejects the address.",
    keineTreffer: "No matches.",
    trefferVonMax: (max: number) => `of max. ${max} matches`,
    externeNichtDurchsuchbar:
      "External contacts are not searchable right now (Salesforce) — you can still find colleagues.",
    tasteEnter: "Enter",
    tasteTab: "Tab",
    tasteRuecktaste: "Backspace",
    hinweisAuswaehlen: "to select ·",
    hinweisUebernimmt: "adds the match",
    hinweisTabSpringt: "moves through the list ·",
    hinweisEntferntLetzten: "removes the last one",
    hinweisEntferntLetztenTeilnehmer: "removes the last attendee",

    chipExtern: "· External",
    chipVerschiebenAria: (name: string, ziel: string) =>
      `Move ${name} to ${ziel}`,
    chipZu: (ziel: string) => `To ${ziel}`,
    chipEntfernenAria: (name: string) => `Remove ${name}`,

    rollePflicht: "Required",
    rolleOptional: "Optional",
    rollePflichtHinweis: "A slot only counts if everyone here is available.",
    rolleOptionalHinweis:
      "Gets invited but blocks no slot — the slot list only says who cannot make it.",
    rollePflichtLeer: "Nobody — only your calendar counts.",
    rolleOptionalLeer: "Nobody optional.",

    externWarnungEiner:
      "1 external attendee — the invitation leaves the company",
    externWarnungMehrere: (n: number) =>
      `${n} external attendees — the invitation leaves the company`,

    pruefenKopf: "Checking attendees …",
    pruefenWird: "is",
    pruefenWerden: "are",
    pruefenRest: (sekunden: number) =>
      `being looked up in Salesforce right now. Sending stays blocked until then; each address is given at most ${sekunden} seconds.`,
    unbekanntIst: "is",
    unbekanntSind: "are",
    unbekanntRest:
      "not stored as a contact in Salesforce. Without a contact record the server rejects the invitation — remove the attendee or create the contact in Salesforce.",
    ungeprueftKopf:
      "The contact check against Salesforce did not get through — whether",
    ungeprueftRest:
      "is stored as a contact could not be verified. Sending stays possible; the server decides.",
  },

  wizard: {
    titel: "Find a time",
    beschreibung: "Find a time everyone is free and send the invitation.",
    schrittTeilnehmerZeitraum: "Attendees & period",
    schrittSlot: "Slot",
    schrittEinladung: "Invitation",
    schrittAbstimmung: "Poll",

    zeitraum: "Period",
    dieseWoche: "This week",
    naechsteWoche: "Next week",
    naechste14Tage: "Next 14 days",
    dauer: "Duration",
    minuten: (n: number) => `${n} min`,

    capOutlookPrueft: "Outlook checks at most",
    capKalender: (n: number) => `${n} calendars`,
    capPflichtMitte: (intern: number, gesamt: number) =>
      `at a time. You and ${intern} internal required attendees are ${gesamt} —`,
    capZuViel: (n: number) => `${n} too many`,
    capEntferne: (n: number) => `. Remove ${n} attendees or move`,
    capIhn: "them",
    capSie: "them",
    capPflichtRest:
      "to Optional — optional attendees are left out of the search but still get the invitation.",
    capOptionalMitte: (belegt: number) =>
      `at a time — you and the required attendees already take up ${belegt} of them.`,
    capOptionalEiner: "The optional attendee is",
    capOptionalMehrere: (n: number) => `The ${n} optional attendees are`,
    capOptionalDeshalb: "therefore",
    capOptionalNichtGeprueft: "not checked for availability",
    capOptionalEinladung: "— the invitation",
    capOptionalBekommtEr: "reaches them",
    capOptionalBekommenSie: "reaches them",
    capOptionalTrotzdem: "anyway.",

    fuer: "For",
    einOptionalerTeilnehmer: "1 optional attendee",
    optionaleTeilnehmer: (n: number) => `${n} optional attendees`,
    einExternerTeilnehmer: "1 external attendee",
    externeTeilnehmer: (n: number) => `${n} external attendees`,
    ist: "there is",
    keineVerfuegbarkeitBekannt: "no known availability",
    optionalUnbekanntRest: (namen: string) =>
      `— Outlook provides no calendar for ${namen}. The suggestions say nothing about that; the invitation`,
    bekommtErOderSie: "reaches them",
    bekommenSie: "reaches them",
    trotzdem: "anyway.",
    externAvailRest:
      "— Outlook sees no calendars outside the company. The suggestions only apply to you and the internal attendees;",
    externAvailBekommt: "gets the invitation anyway.",

    nurDeinKalender: "Only your calendar is checked.",
    pflichtteilnehmerGeprueft: (n: number) =>
      `Checking common free time with ${n} internal required attendee${n > 1 ? "s" : ""}.`,
    optionaleMitgelesen: (n: number) =>
      ` ${n} optional calendars are read along — they block no slot.`,
    vorschlaegeZwischen: (von: string, bis: string) =>
      `Suggestions between ${von} and ${bis} — changeable in Settings under "Calendar".`,
    geschaeftszeitenNichtGeladen:
      "Your business hours could not be loaded right now — the suggestions still follow your account setting.",
    zuVielePflichtteilnehmer: (max: number) =>
      `Too many required attendees for the availability check (max. ${max} calendars)`,
    freieSlotsSuchen: "Search free slots",

    kundenzeitenKopf: "Enter times given by the customer.",
    kundenzeitenRest:
      "If the customer named times themselves, they go in here — also outside your business hours. Next to each time you then see whether the internal attendees are free.",
    datum: "Date",
    bitteTtMmJjjj: "Please enter as TT.MM.JJJJ",
    platzhalterDatum: "TT.MM.JJJJ",
    ariaKundenzeitDatum: "Date of the customer time (TT.MM.JJJJ)",
    beginn: "Start",
    ariaKundenzeitUhrzeit: "Time of the customer time",
    zeitUebernehmen: "Add time",
    dauerWieSchrittEins: (dauer: string) =>
      `Duration ${dauer} — as set in step 1. All times put up for choice are equally long.`,
    bitteDatumEintragen: "Please enter a date as TT.MM.JJJJ.",
    zeitSchonInListe: "This time is already in the list.",
    mehrAlsNZeiten: (max: number) => `More than ${max} times are not possible.`,
    mehrAlsNZeitenEntfernen: (max: number) =>
      `More than ${max} times are not possible — remove one first.`,
    terminInVergangenheit:
      "The time lies in the past — it can no longer be voted on.",
    eingetrageneZeiten: (n: number, dauer: number) =>
      `${n} entered ${n === 1 ? "time" : "times"} · ${dauer} min`,

    verfuegbarkeitWirdGeprueft: "Checking availability …",
    verfuegbarkeitUnbekannt: "Availability unknown",
    istBelegt: (namen: string, anzahl: number) =>
      `${namen} ${anzahl === 1 ? "is" : "are"} busy`,
    internFreiAber: (namen: string, anzahl: number) =>
      `Internally free — ${namen} (optional) ${anzahl === 1 ? "is" : "are"} unavailable`,
    freiSoweitKalender: "Free as far as calendars are known",
    internFrei: "Internally free",
    du: "You",

    optionalUnbekannt: "Optional: unknown",
    optionalKannNicht: (namen: string, anzahl: number) =>
      `${namen} ${anzahl === 1 ? "is" : "are"} unavailable`,
    alleMitBekanntemKalender: "Everyone with a known calendar is free",
    alleKoennen: "Everyone is free",

    slotGesperrt: (max: number) =>
      `At most ${max} suggestions — deselect one first`,
    zeitEntfernenAria: (datum: string, zeit: string) =>
      `Remove time ${datum} ${zeit}`,

    slotsWerdenGesucht: "Searching for free time slots …",
    fehlerNichtEinladbar:
      "At least one attendee address cannot be invited (neither an active bitop colleague nor a Salesforce contact).",
    fehlerNichtPruefbar:
      "At least one attendee address cannot be checked (the slot search only knows internal calendars).",
    fehlerDienstSuche: "The calendar service is unavailable right now.",
    fehlerFormatSuche:
      "The request to the calendar service was malformed (technical error). Please report this — it is not down to the attendees.",
    fehlerSuche: "The slot search failed.",
    teilnehmerPruefen: "Check attendees",
    keineGemeinsamenSlots: "No common free slots in the chosen period.",
    kundenzeitTrotzdem:
      "You can still enter a time named by the customer above — also outside your business hours.",
    zeitraumErweitern: "Widen the period",
    slotZaehlerGekappt: (gezeigt: number, gesamt: number, dauer: number) =>
      `${gezeigt} of ${gesamt} free time slots · ${dauer} min`,
    slotZaehler: (gesamt: number, dauer: number) =>
      `${gesamt} free time slots · ${dauer} min`,
    optionaleNichtsGemeldetKopf:
      "The calendar service reported nothing about the optional attendees — whether they are free is",
    offen: "open",
    optionaleNichtsGemeldetRest:
      ". The suggestions apply to you and the required attendees.",

    mehrereTermineWaehlbar: "Several times selectable",
    hoechstensN: (max: number) =>
      `(at most ${max}) — the attendees get them put up for choice.`,
    vonN: (gewaehlt: number, max: number) => `${gewaehlt} of ${max}`,
    gewaehlt: "selected —",
    nochKeinerAusgewaehlt: "none is selected yet.",
    nTermineZurAbstimmung: (n: number) =>
      `the ${n} times go to a poll, nothing is booked yet.`,
    einzelnerTerminGebucht: "a single time is booked directly.",
    capVorschlaegeKopf: (max: number) =>
      `More than ${max} suggestions are not possible — every further option costs the recipient a calendar check and one answer less often. For a different time, first`,
    abwaehlen: "deselect",
    nochNVorschlaege: (n: number) => `Show ${n} more suggestions`,
    weiter: "Next",

    betreff: "Subject",
    betreffPlatzhalter: "e.g. registration alignment",
    beschreibungFeld: "Description",
    beschreibungPlatzhalter: "Short context for the invitation …",
    zusammenfassung: "Summary",
    duOrganisator: "You (organiser)",
    keineWeiterenTeilnehmer: " — no other attendees",
    optionalListe: (namen: string) => `Optional: ${namen}`,
    externSuffix: (firma: string | null) =>
      firma ? ` (external, ${firma})` : " (external)",
    nochNichtsGebucht: "Nothing is booked yet.",
    nochNichtsGebuchtRest: (n: number) =>
      `These ${n} times go to the attendees as a choice; booking happens only once they have answered. Until then all ${n} times stay free in your calendar.`,
    brauchtPflichtKopf: "A poll needs at least one",
    pflichtTeilnehmer: "required attendee",
    brauchtPflichtRest:
      "— otherwise no calendar stands behind the acceptance. Move someone from Optional to Required in step 1.",
    internRundeKopf:
      "This round has internal attendees only. As soon as all required attendees have accepted,",
    internRundeFett: "exactly these people are booked",
    internRundeRest:
      "— a customer who proposed the times gets no invitation. Add them as an attendee in step 1 if they should be there.",
    betreffFehltNoch: "Subject still missing",
    fehlerExtern:
      "At least one attendee address is external — there is no availability for it here.",
    fehlerDienstSenden: "Calendar service unavailable. Please try again.",
    fehlerFormatSenden: (mitAbstimmung: boolean) =>
      `The request was malformed (technical error). Nothing was sent${
        mitAbstimmung ? " and no poll was created" : ""
      } — it is not down to the attendees. Please report this.`,
    fehlerAbstimmung: "The poll could not be created. Nothing was sent.",
    fehlerEinladung: "The invitation could not be sent.",
    rueckfrageAbstimmung: (n: number) => `Put ${n} times up for a poll now?`,
    rueckfrageSenden: "Send the invitation now?",
    jaZurAbstimmung: "Yes, put to a poll",
    jaSenden: "Yes, send",
    zurAbstimmungStellen: "Put up for a poll",
    einladungSenden: "Send invitation",
    titelNichtEinladbar: "Address in the attendee list cannot be invited",
    titelWirdGeprueft: "Attendees are still being checked in Salesforce",
    titelBrauchtPflicht: "A poll needs at least one required attendee",
    toastEinladungGesendet: "Invitation sent",
    toastTerminErstellt: "The event has been created.",
    toastAbstimmungAngelegt: "Poll created",
    toastTermineZurWahl: "The times are up for choice.",

    zurAbstimmungGestellt: "Put up for a poll",
    termineStehenZurWahl: (n: number) => `${n} times are up for choice —`,
    gebuchtIstNochNichts: "nothing is booked yet.",
    laeuftBis: "The poll runs until",
    laeuftBisRest: (zeit: string) =>
      `, ${zeit} Uhr — that is until the earliest suggestion; after that it would be moot. Once the attendees have answered, the event is booked.`,
    einladungEinerBekommen:
      "The attendee has received their personal poll link by mail.",
    einladungAlleBekommen: (n: number) =>
      `All ${n} attendees have received their personal poll link by mail.`,
    einladungenFehlgeschlagen: (fehl: number, gesamt: number) =>
      `${fehl} of ${gesamt} invitations did not go out`,
    einladungenAbstimmungSteht: (adressen: string) =>
      `(${adressen}). The poll stands —`,
    dieserEmpfaengerWeiss: "this recipient does not",
    dieseEmpfaengerWissen: "these recipients do not",
    nochNichtsDavon: "know about it yet.",
    keineMailKopf:
      "Atlas sends no mail about incoming answers — you find the current state under",
    abstimmungenLink: "Polls",
    keineMailRest: ", at any time later via the calendar toolbar as well.",

    terminErstellt: "Event created",
    einladungVersendet: "The invitation has been sent to all attendees.",
    teamsMeetingOeffnen: "Open Teams meeting",
    inOutlookOeffnen: "Open in Outlook",
  },

  polls: {
    dokumentTitel: "Meeting polls – Atlas",
    seitenTitel: "Meeting polls",
    seitenBeschreibung: "Who has answered, and what happens next.",

    zustandGebucht: "Booked",
    zustandWirdGebucht: "Booking",
    zustandBuchungFehlgeschlagen: "Booking failed",
    zustandKeinGemeinsamerTermin: "No common time",
    zustandAbgesagt: "Cancelled",
    zustandFristAbgelaufen: "Deadline passed",
    zustandLaeuft: "Running",

    stoerungTitel: "Polls unavailable",
    stoerungHinweis:
      "The state of your meeting polls cannot be loaded right now. Please try again in a moment.",
    leerTitel: "No meeting poll yet",
    leerHinweis:
      "As soon as you put several times up for choice in the calendar, their state appears here.",
    zumKalender: "Go to calendar",

    laeuftNoch: "Still running",
    laeuftNochLeer: "No poll is running right now.",
    abgeschlossen: "Completed",
    habenAbgestimmt: (geantwortet: number, gesamt: number) =>
      `${geantwortet} of ${gesamt} have voted`,
    vorschlaege: (n: number) => `${n} suggestions`,
    alleHabenAbgestimmt: "Everyone has voted.",
    eineRueckmeldungFehlt: "1 answer is still missing.",
    rueckmeldungenFehlen: (n: number) => `${n} answers are still missing.`,
    frist: "Deadline:",
    fristZeit: (zeit: string) => `, ${zeit} Uhr`,
    uhrSuffix: " Uhr",
    // „Deadline" gross: es ist die Beschriftung hinter dem „·"-Trenner,
    // genau wie `frist` in der Listen-Karte.
    dauerUndFrist: (minuten: number) => `${minuten} minutes · Deadline`,

    alleAbstimmungen: "All polls",
    gibtEsNichtTitel: "This poll does not exist",
    gibtEsNichtHinweis:
      "It was deleted, or it belongs to a different mailbox.",
    buchungsfehler: (grund: string) => `Error while booking: ${grund}`,

    kennzahlEingeladen: "Invited",
    kennzahlAbgestimmt: "Voted",
    kennzahlOffen: "Open",
    zustimmungJeVorschlag: "Support per suggestion",
    nochKeineZusage: "No acceptance yet",
    niemandAbgestimmt: "Nobody has voted via the link so far.",
    niemandAbgestimmtOffen:
      "That is normal while the deadline is running — people often answer only shortly before.",
    niemandAbgestimmtBeendet:
      "Anyone who answered by e-mail does not show up here: only what comes in via the personal link is counted.",

    eingeladeneAnzahl: (n: number) => `Invited (${n})`,
    optionalChip: "optional",
    einladungNichtZugestellt: "The invitation could not be delivered",
    erinnertAm: (zeitpunkt: string) => `Reminded on ${zeitpunkt} Uhr`,
    kannKeinen: "None possible",
    // „1 time" hiesse im Englischen „einmal". Gemeint ist, WIE VIELE der
    // vorgeschlagenen Zeiten dieser Teilnehmer angekreuzt hat.
    eineZeit: "1 slot",
    nZeiten: (n: number) => `${n} slots`,
    keineAntwort: "No answer",
    keineAntwortErklaerung:
      '"No answer" means: nothing came in via the personal link. Atlas does not see an answer sent as an ordinary e-mail — that one is in your mailbox.',
    gebuchtenTerminOeffnen: "Open the booked event in Outlook",
  },

  aufgaben: {
    titel: "Calendar",
    perDragVerschieben: "Move tasks by dragging",
    neuLaden: "reload",
    zurueck14Tage: "back 14 days",
    vor14Tage: "forward 14 days",
    heuteKlein: "today",
    heute: "Today",
    nurMeine: "Mine only",
    erledigte: "Done",
    salesforce: "Salesforce",
    vorherigerMonat: "previous month",
    naechsterMonat: "next month",
    anzahlAufgaben: (n: number) => `${n} tasks`,
    weitere: (n: number) => `+${n} more`,
    keineTermine: "no events",
    ladenFehlgeschlagen: "Loading failed",
    sfNichtVerschiebbar:
      "Salesforce tasks cannot be moved — edit them in Salesforce.",
    verschiebenFehlgeschlagen: (grund: string) => `Moving failed: ${grund}`,
  },
};
